import React, { useState, useEffect, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';
import YandexMap from 'src/components/YandexMap';
import { Spinner } from 'src/components/animations/Spinner';
import classnames from 'classnames';
import type { YMap } from '@yandex/ymaps3-types';
import type {
  Coordinates,
  ShortHotelInfo,
} from 'src/components/YandexMap/types';
import { useTranslation } from '@lt/localization';
import ErrorBoundary from 'src/error/components/ErrorBoundary';
import EmptyBlock from 'src/routes/Hotel/components/EmptyBlock';
import { loadReactComponent } from 'src/components/YandexMap/loaders/componentLoaders';
import { loadModulePromise } from 'src/components/YandexMap/mapLoader';
import { DEFAULT_EASING_DURATION } from 'src/components/YandexMap/constants';
import useShortPress from 'src/components/YandexMap/hooks/useShortPress';
import Clusterer from 'src/components/YandexMap/components/Clusterer';
import { calculateMapCenter } from 'src/components/YandexMap/utils/calculateMapCenter';
import type { PropsFromRedux } from '.';
import Controls from './components/Controls';
import { ACTIVE_HOTEL_ZOOM } from './constants';
import { pushMapError, pushMapExpanded, pushMapOpened } from './analytics';
import { DEFAULT_MAP_POSITION } from '../../store/map/constants';
import ActiveHotelLayer from './components/ActiveHotelLayer';
import HotelPopup from './components/HotelPopup';
import Point from './components/Point';

import styles from './yandexMapsSearch.module.css';

const YMapListener = loadReactComponent('YMapListener', loadModulePromise);

const YandexMapsSearch = (props: PropsFromRedux) => {
  const {
    hotelsList,
    activeHotel,
    mapPosition,
    isMobile,
    getMapDataHandler,
    isExpanded,
    toggleHandler,
    resetPopupDataHandler,
    hotelInfoForPopup,
  } = props;

  const { t } = useTranslation();

  const [fetchingMapInfo, setFetchingMapInfo] = useState(true);

  const [location, setLocation] = useState(mapPosition?.coordinates);
  const [bounds, setBounds] = useState(mapPosition?.bounds);
  const [zoom, setZoom] = useState(mapPosition.zoom);

  const readonlyLocation = useRef(mapPosition.coordinates);
  const readonlyZoom = useRef(mapPosition.zoom);

  const points = useMemo(
    () =>
      hotelsList.map((hotel) => {
        const { coordinates, ...otherHotelInfo } = hotel.properties;
        return {
          coordinates: coordinates as [number, number],
          shortHotelInfo: otherHotelInfo as ShortHotelInfo,
        };
      }),
    [hotelsList],
  );

  // После маунта компонента дергаем ручку для получения границ региона
  useEffect(() => {
    getMapDataHandler();
  }, [getMapDataHandler]);

  // После прихода данных по границам региона или центру региона устанавливаем их
  useEffect(() => {
    if (!fetchingMapInfo) return;

    if (!isEqual(mapPosition?.coordinates, DEFAULT_MAP_POSITION)) {
      setLocation(mapPosition.coordinates);
      setZoom(mapPosition.zoom);
      // Учитываем, что карта перемещается не моментально
      setTimeout(() => setFetchingMapInfo(false), DEFAULT_EASING_DURATION);
    }
    if (mapPosition?.bounds) {
      setBounds(mapPosition.bounds);
      // Учитываем, что карта перемещается не моментально
      setTimeout(() => setFetchingMapInfo(false), DEFAULT_EASING_DURATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapPosition]);

  // Зум на отель при наведении на карточку в поиске
  // При этом убираем bounds, так как при использовании bounds карта не учитывает center
  useEffect(() => {
    if (activeHotel && activeHotel.zoomRequired) {
      // Сбрасываем bounds, потому что с ними не работает зум по клику на активный отель
      if (bounds) {
        setBounds(undefined);
      }
      setLocation(activeHotel.coordinates);
      setZoom(ACTIVE_HOTEL_ZOOM);
    }
  }, [activeHotel, setBounds, bounds]);

  useEffect(() => {
    pushMapOpened();
  }, []);

  // Функция для отслеживания движения юзера по карте
  const onUpdate = React.useCallback((params) => {
    const { mapInAction, location: currentLocation } = params;
    if (!mapInAction) {
      readonlyLocation.current = currentLocation.center;
      readonlyZoom.current = currentLocation.zoom;
    }
  }, []);

  const mapRef = useRef<YMap>(null);

  /**
   * Перемещает позицию карты
   * @param coordinates Координаты центра карты
   * @param zoomDiff Разница в зуме, может быть положительной и отрицательной
   * @param offset Отступ
   */
  const moveToPoint = (
    coordinates: Coordinates,
    zoomDiff: number,
    offset: { x: number; y: number },
  ) => {
    // Сбрасываем bounds, потому что с ними не работает зум по клику на кластер
    if (bounds) {
      setBounds(undefined);
    }

    if (!mapRef.current) return;

    // Яндекс не умеет в оффсеты в версии 3.0, поэтому перевычисляем центр карты с учетом отступа
    const coordinatesWithOffset = calculateMapCenter(
      mapRef.current?.bounds[0],
      mapRef.current?.bounds[1],
      coordinates,
      mapRef.current?.size,
      offset,
    );

    setLocation(coordinatesWithOffset);
    setZoom(readonlyZoom.current + zoomDiff);
  };

  const changeZoom = (zoomStep: number) => {
    if (bounds) {
      setBounds(undefined);
    }
    setZoom(readonlyZoom.current + zoomStep);
  };

  const popupRef = useRef<HTMLDivElement>(null);

  const adjustMapToCurrentPosition = () => {
    setLocation(readonlyLocation.current);
    setZoom(readonlyZoom.current);
  };

  const toggleExpandMap = () => {
    toggleHandler();
    pushMapExpanded(isExpanded);
    adjustMapToCurrentPosition();
  };

  const onCloseHotelCard = () => {
    resetPopupDataHandler();
    adjustMapToCurrentPosition();
  };

  // Закрываем попап, когда кликаем по карте снаружи попапа
  useShortPress(popupRef, () => {
    if (hotelInfoForPopup) {
      onCloseHotelCard();
    }
  });

  return (
    <ErrorBoundary
      onError={pushMapError}
      fallback={
        <EmptyBlock
          isMobile
          title={t('map.static.message.title')}
          description={t('map.static.message.description')}
        />
      }
      showDialog={false}
    >
      <div
        className={classnames(styles.yandexMapSearchContainer, {
          [styles.mobileYandexMapSearchContainer]: isMobile,
        })}
      >
        {fetchingMapInfo && (
          <div
            className={classnames(styles.mapLoaderBg, {
              [styles.mobileMapLoaderBg]: isMobile,
            })}
          >
            <Spinner size={64} />
          </div>
        )}
        <YandexMap
          isMobile={isMobile}
          center={location}
          bounds={bounds}
          zoom={zoom}
          mapRef={mapRef}
        >
          <YMapListener onUpdate={onUpdate} />
          <Controls
            changeZoom={changeZoom}
            isMobile={isMobile}
            isExpanded={isExpanded}
            toggleHandler={toggleExpandMap}
          />

          <ActiveHotelLayer
            moveToPoint={moveToPoint}
            coordinates={activeHotel?.coordinates}
          />

          <Clusterer
            isMobile={isMobile}
            moveToPoint={moveToPoint}
            activeHotel={activeHotel}
            points={points}
            Point={Point}
          />
          <HotelPopup popupRef={popupRef} onClosePopup={onCloseHotelCard} />
        </YandexMap>
      </div>
    </ErrorBoundary>
  );
};

export default YandexMapsSearch;
